// The MatrixQuestionRows document written when an MSI effect is adopted.
//
// Extracted from AddEffectFlow so the payload can be tested without Firestore:
// `ID` and `Timestamp` are injected rather than generated here, which is what
// keeps this pure.
//
// The row is the tenant's own snapshot of a catalogue statement — answers point
// at it by RowID and never at the catalogue. That makes it the right place for
// the provenance stamp: it is written once, at adoption, and every answer given
// under that wording inherits it.
//
// Four fields together resolve to exactly one statement:
//   Standaard + StandaardVersion  →  which immutable published document
//   EffectUid + StellingNummer    →  which statement inside it
//
// `StellingNummer` is positional, which is only safe because StandaardVersion
// pins the document it indexes into. Without the version it would be the exact
// footgun that damaged us before — a reworded statement keeps its number.
//
// AI-CONTEXT: See docs/features/msi-version-stamping.md
import legacyStandaardFor, { legacySectorForEffect } from './legacyStandaardVersions'
import internalStandaardFor from './internalStandaardVersions'

/**
 * @typedef {object} AdoptedEffect
 * @property {string} [id] - MSIId of the effect (the document's `uid` when it has one)
 * @property {string} [standaard] - Sector slug of the standaard, or absent for the legacy catalogue
 * @property {string} [standaardVersion] - Pinned version, or absent for the legacy catalogue
 * @property {Array<string>} [sectors] - Sectors of the effect; the legacy version is derived from these
 *
 * @typedef {object} AdoptedQuestion
 * @property {string} [id] - MSIId of the statement
 * @property {string} [name] - Statement text
 * @property {'positive'|'negative'} [posNeg]
 * @property {number} [stellingNummer] - Position within its effect; absent for legacy questions
 * @property {string} [targetGroupsMode]
 * @property {Array<string>} [targetGroups]
 */

/**
 * The four provenance fields, on their own.
 *
 * Shared because three separate paths materialise MatrixQuestionRows — the
 * picker (AddEffectFlow), Decca (saveMsiEffect) and the measurement import
 * (buildImportPlan). Each has a different row shape but the same stamp; a copy
 * per path would drift.
 *
 * @param {AdoptedEffect} effect
 * @param {AdoptedQuestion} question
 * @returns {{Standaard: string, StandaardVersion: string, EffectUid: string, StellingNummer: number|null}}
 */
const buildProvenanceStamp = (effect, question) => {
    const e = effect || {}
    const q = question || {}

    // A versioned document states its own standaard and version. Only fall back
    // to the asserted maps when it does not — never the other way around.
    //
    // The eigen-standaard map is consulted before the legacy one because it is
    // keyed on the effect itself rather than on a sector, which is both more
    // specific and the only handle these effects have: they carry no sector at
    // all, so a sector rule can never reach them.
    const internal = e.standaard ? null : internalStandaardFor(e.id)
    // `e.sectors` eerst; valt daar niets uit af te leiden, dan de opgegeven
    // sector van dit effect. Vier /database-effecten dragen nergens sectors.
    const legacy = (e.standaard || internal)
        ? null
        : legacyStandaardFor(e.sectors) || legacyStandaardFor([legacySectorForEffect(e.id)])
    const derived = internal || legacy

    return {
        Standaard: e.standaard || (derived ? derived.standaard : ''),
        StandaardVersion: e.standaardVersion || (derived ? derived.standaardVersion : ''),
        // Legacy statements have no slug and no per-statement identity, but
        // their question uuids are already globally unique — MSIId alone
        // identifies them, so these two stay empty rather than invented.
        EffectUid: e.standaard && e.id ? e.id : '',
        StellingNummer: Number.isFinite(q.stellingNummer) ? q.stellingNummer : null,
    }
}

/**
 * Builds one MatrixQuestionRows document.
 *
 * Absent coordinates are written as `''` / `null` rather than omitted: the
 * legacy catalogue genuinely has no per-statement identity, and an explicit
 * empty says so, where a missing key would also mean "never stamped". A backfill
 * has to tell those apart. Never `undefined` — Firestore rejects that outright.
 *
 * @param {object} params
 * @param {AdoptedEffect} params.effect - Effect as the picker adapter hands it over
 * @param {AdoptedQuestion} params.question - One statement of that effect
 * @param {number} params.index - Zero-based order within the matrix
 * @param {string} params.rowId - Pre-generated uuid for the row's business key
 * @param {string} params.fieldId - Parent QuestionnaireFields.ID
 * @param {string} params.client - Tenant id (CompagnyID)
 * @param {*} params.timestamp - Firestore server timestamp
 * @param {string} params.color - Accent colour for the row
 * @param {string} params.targetGroup - Selected meetstandaard target group
 * @returns {object} A MatrixQuestionRows document, safe to hand to Firestore
 */
const buildMatrixRowPayload = ({
    effect,
    question,
    index,
    rowId,
    fieldId,
    client,
    timestamp,
    color,
    targetGroup,
}) => {
    const e = effect || {}
    const q = question || {}

    return {
        Title: q.name || '',
        Field: fieldId || '',
        Compagny: client || '',
        CompagnyID: client || '',
        Timestamp: timestamp,
        Position: index + 1,
        ID: rowId || '',
        MSIId: q.id || '',
        Color: color || '',
        // Legacy rows store boolean false for "not negative"; only the string
        // 'negative' ever flips a score. Tightening that is a separate decision
        // from stamping a version, so the existing fallback stays.
        posNeg: q.posNeg ? q.posNeg : false,
        TargetGroup: targetGroup || '',
        targetGroupsMode: q.targetGroupsMode || 'all',
        targetGroups: Array.isArray(q.targetGroups) ? q.targetGroups : [],
        // Provenance. A versioned effect brings its own standaard + version
        // from the document; a legacy one has them derived from its sector,
        // because /database publishes no version at all. Unknown sector →
        // empty, never a default: a wrong stamp hides, a missing one shows.
        ...buildProvenanceStamp(e, q),
    }
}

export default buildMatrixRowPayload
export { buildProvenanceStamp }
